<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="4" style="max-width: 500px; margin-top: 40px">
        <v-card>
          <v-toolbar>
            <v-toolbar-title>Change Password</v-toolbar-title>
            <v-spacer />
            <v-icon>mdi-key</v-icon>
          </v-toolbar>
          <v-card-text style="padding-bottom: 14px">
            <v-form ref="form" lazy-validation v-on:submit.prevent="">
              <v-text-field
                id="passwordCurrent"
                label="Current Password"
                name="passwordCurrent"
                prepend-icon="mdi-lock-outline"
                v-model="passwordCurrent"
                v-on:keyup.enter="changePassword"
                :error-messages="passwordCurrentErrors"
                @change="$v.passwordCurrent.$touch()"
                @blur="$v.passwordCurrent.$touch()"
                @input="$v.passwordCurrent.$touch()"
                :append-icon="
                  passwordCurrentFieldType ? 'mdi-eye-off' : 'mdi-eye'
                "
                @click:append="
                  () => (passwordCurrentFieldType = !passwordCurrentFieldType)
                "
                :type="passwordCurrentFieldType ? 'password' : 'text'"
              />
              <v-text-field
                id="password"
                label="New Password"
                name="password"
                prepend-icon="mdi-lock"
                v-model="password"
                v-on:keyup.enter="changePassword"
                :error-messages="passwordErrors"
                @change="$v.password.$touch()"
                @blur="$v.password.$touch()"
                @input="$v.password.$touch()"
                :append-icon="passwordFieldType ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="() => (passwordFieldType = !passwordFieldType)"
                :type="passwordFieldType ? 'password' : 'text'"
              />
              <v-text-field
                id="passwordConfirmation"
                label="Confirm New Password"
                name="passwordConfirmation"
                prepend-icon="mdi-lock-check"
                v-model="passwordConfirmation"
                v-on:keyup.enter="changePassword"
                :error-messages="passwordConfirmationErrors"
                @change="$v.passwordConfirmation.$touch()"
                @blur="$v.passwordConfirmation.$touch()"
                @input="$v.passwordConfirmation.$touch()"
                :append-icon="
                  passwordConfirmFieldType ? 'mdi-eye-off' : 'mdi-eye'
                "
                @click:append="
                  () => (passwordConfirmFieldType = !passwordConfirmFieldType)
                "
                :type="passwordConfirmFieldType ? 'password' : 'text'"
              />
            </v-form>
          </v-card-text>
          <v-card-actions
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 8px 16px 16px 16px;
            "
          >
            <v-btn depressed @click="back" :disabled="loading" color="warning"
              >back</v-btn
            >
            <v-btn
              color="primary"
              dark
              style="color: white"
              @click="changePassword"
              >Save</v-btn
            >
          </v-card-actions>
          <v-progress-linear
            :active="loading"
            :indeterminate="loading"
            absolute
            bottom
            color="red lighten-2"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { sameAs } from 'vuelidate/lib/validators';
import CustomEncryption from '../../lib/CustomEncryption';
import { dataExtraString3 } from '../../store/params';

export default {
  mixins: [validationMixin],
  validations: {
    password: {
      required(value) {
        return value != '';
      },
      minLength(value) {
        return value.length >= 12;
      },
      upperCase(value) {
        const pattern = /[A-Z]/;
        return !!pattern.test(value);
      },
      lowerCase(value) {
        const pattern = /[a-z]/;
        return !!pattern.test(value);
      },
      specialChar(value) {
        const pattern = /[\x21-\x2f\x3A-\x40\x5B-\x60\x7B-\x7E]/;
        return !!pattern.test(value);
      },
      numeric(value) {
        const pattern = /\d/;
        return !!pattern.test(value);
      },
    },
    passwordConfirmation: {
      required(value) {
        return value != '';
      },
      sameAsPassword: sameAs('password'),
    },
    passwordCurrent: {
      required(value) {
        return value != '';
      },
    },
  },
  data() {
    return {
      passwordFieldType: true,
      passwordConfirmFieldType: true,
      passwordCurrentFieldType: true,
      password: '',
      passwordConfirmation: '',
      passwordCurrent: '',
      passChanged: false,
    };
  },

  computed: {
    loading() {
      const loading =
        this.$store.state.loading || this.$store.state.logoutLoading;
      return loading;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      if (!this.$v.password.required) errors.push('New Password is required');
      if (!this.$v.password.lowerCase)
        errors.push('New Password must have lowercase character');
      if (!this.$v.password.upperCase)
        errors.push('New Password must have uppercase character');
      if (!this.$v.password.numeric)
        errors.push('New Password must have numeric character');
      if (!this.$v.password.specialChar)
        errors.push('New Password must have special character');

      if (!this.$v.password.minLength)
        errors.push('New Password must be at least 12 characters long');
      return errors;
    },
    passwordConfirmationErrors() {
      const errors = [];
      if (!this.$v.passwordConfirmation.$dirty) return errors;
      if (!this.$v.passwordConfirmation.required)
        errors.push('Password Confirmation is required');
      if (!this.$v.passwordConfirmation.sameAsPassword)
        errors.push('Passwords must be identical.');
      return errors;
    },
    passwordCurrentErrors() {
      const errors = [];
      if (!this.$v.passwordCurrent.$dirty) return errors;
      if (!this.$v.passwordCurrent.required)
        errors.push('Current Password is required');
      return errors;
    },
  },

  methods: {
    async changePassword() {
      this.$v.$touch();
      if (!this.$v.$invalid && !this.loading) {
        // eslint-disable-next-line new-cap
        const speEncrypt = new CustomEncryption();
        const extras = speEncrypt.decryption({
          enc: process.env.VUE_APP_STRING_EXTRA2,
          masterkey: dataExtraString3,
        });
        this.$store
          .dispatch('changeForcePassword', {
            password: this.password,
            passwordConfirmation: this.passwordConfirmation,
            passwordCurrent: this.passwordCurrent,
            encryptedPassword: speEncrypt.encrypt(
              speEncrypt.encryption(this.password),
              '',
              extras
            ),
            encryptedPasswordConfirmation: speEncrypt.encrypt(
              speEncrypt.encryption(this.passwordConfirmation),
              '',
              extras
            ),
            encryptedPasswordCurrent: speEncrypt.encrypt(
              speEncrypt.encryption(this.passwordCurrent),
              '',
              extras
            ),
          })
          .then((res) => {
            if (res) {
              this.$router.push('/');
            }
          });
      }
    },

    back() {
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.v-sheet.v-toolbar:not(.v-sheet--outlined) {
  box-shadow: 0px 0px 1px 0px;
}
</style>
